import { dollarsWithCentsWithSymbol } from 'helpers/currency';
import { useRouter } from 'next/router';
import parseZone from 'helpers/parseZone';
import BookingActionsBox from 'components/ui/BookingActionsBox/BookingActionsBox';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import usePreApproveCardShow from '../hooks/usePreApproveCardShow';
import { SpecialMessageProps } from './types';
import DefaultMessage from './DefaultMessage';

export default function EditInquiryMessage({ msg, lastEdits, index, booking }: SpecialMessageProps) {
  // checks for last owner edit, intialized state, if the last edit was from owner and not from renter
  const router = useRouter();
  const [, hidePreApproveBox] = usePreApproveCardShow();
  const rudderTrack = useRudderStackTrack();

  const bookingModifications = msg?.booking_modification;

  if (lastEdits.owner !== index || booking.state !== 'initialized' || lastEdits.renter > lastEdits.owner)
    return <DefaultMessage message={msg} />;

  const bookNowAction = () => {
    rudderTrack('booking_action_book_now', {
      bookingId: booking.id,
      bookingState: booking.state,
      action: 'book_now_edited_inquiry',
      location: 'conversation_action_box',
    });

    router.push(`/a/bookings/${booking.id}/book_now`);
  };

  return (
    <BookingActionsBox
      title={<span>{booking?.owner?.first_name} has edited your inquiry</span>}
      ctaText="Book now"
      subHeading={getEditInquiryMessage(bookingModifications, booking?.owner?.first_name, booking)}
      userType="renter"
      onSubmit={bookNowAction}
      showCloseButton={false}
      showSubtitle={false}
      onClose={hidePreApproveBox}
    />
  );
}

const getEditInquiryMessage = (bookingModifications, first_name, booking) => {
  if (!bookingModifications) return '';

  const priceChange =
    bookingModifications.special_offer_price !== bookingModifications.previous_special_offer_price
      ? `price to ${dollarsWithCentsWithSymbol(bookingModifications.special_offer_price + booking.boat_price)}`
      : '';

  const prevStart = parseZone(bookingModifications.previous_trip_start);

  const start = parseZone(bookingModifications.trip_start);

  const timeChange = prevStart.diff(start, 'hour') !== 0 ? `start time to ${start.format('h:mm A')}` : '';
  const dateChange = prevStart.diff(start, 'day') !== 0 ? `start date to ${start.format('MMM D, YYYY')}` : '';

  return `${first_name} has changed the ${formatArray([dateChange, timeChange, priceChange].filter((e) => !!e))}.`;
};

const formatArray = (arr) => {
  let outStr = '';
  if (arr.length === 1) {
    // eslint-disable-next-line prefer-destructuring
    outStr = arr[0];
  } else if (arr.length === 2) {
    // joins all with "and" but no commas
    // example: "bob and sam"
    outStr = arr.join(' and ');
  } else if (arr.length > 2) {
    // joins all with commas, but last one gets ", and" (oxford comma!)
    // example: "bob, joe, and sam"
    outStr = `${arr.slice(0, -1).join(', ')}, and ${arr.slice(-1)}`;
  }
  return outStr;
};
